import { useEffect, useState } from "react";
import AdminLayout from "../../layouts/AdminLayout";
import DataTableLayout from "../../layouts/DataTableLayout";
import DataTable from "../../components/common/DataTable";
import Button from "../../components/ui/Button";
import { Plus } from "lucide-react";
import FormField from "../../components/ui/FormField";
import TextInput from "../../components/ui/TextInput";
import SelectDropdown from "../../components/ui/SelectDropdown";
import ActionButtons from "../../components/common/ActionButtons";
import Modal from "../../components/ui/Modal";
import DeleteConfirmationModal from "../../components/common/DeleteConfirmationModal";
import Skeleton from "../../components/ui/Skeleton";
import FilterBar from "../../components/common/FilterBar";
import { toast } from "react-toastify";

import {
  getServices,
  createService,
  updateService,
  deleteService,
} from "../../services/serviceService";
import { getSocieties } from "../../services/societyService";

const Services = () => {
  const [servicesData, setServicesData] = useState([]);
  const [societies, setSocieties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openModal, setOpenModal] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [serviceFormPayload, setServiceFormPayload] = useState({
    service_name: "",
    society_id: "",
  });

  const [filters, setFilters] = useState({
    search: "",
    society_id: "",
  });
  const [appliedFilters, setAppliedFilters] = useState({
    search: "",
    society_id: "",
  });

  const fetchServices = async () => {
    try {
      const [servicesResult, societiesResult] = await Promise.all([
        getServices(),
        getSocieties(),
      ]);
      setServicesData(servicesResult || []);
      setSocieties(societiesResult || []);
    } catch (err) {
      console.error("Error fetching services:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchServices();
  }, []);

  const filteredData = (Array.isArray(servicesData) ? servicesData : []).filter((item) => {
    const search = (appliedFilters.search || "").toLowerCase();
    const targetSociety = appliedFilters.society_id;

    const matchesSearch = !search || (item.service_name || "").toLowerCase().includes(search);
    let matchesSociety = true;
    if (targetSociety === "global") {
      matchesSociety = item.society_id == null;
    } else if (targetSociety) {
      matchesSociety = String(item.society_id) === String(targetSociety);
    }

    return matchesSearch && matchesSociety;
  });

  const handleAdd = () => {
    setSelectedService(null);
    setServiceFormPayload({ service_name: "", society_id: "" });
    setOpenModal(true);
  };

  const handleEdit = (row) => {
    setSelectedService(row);
    setServiceFormPayload({
      service_name: row.service_name,
      society_id: row.society_id || "",
    });
    setOpenModal(true);
  };

  const handleDelete = (row) => {
    setDeleteTarget({
      row,
      type: "Service",
      id: row.service_id,
      name: row.service_name || `Service #${row.service_id}`,
      canDelete: true,
      actionLabel: "Delete",
    });
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget?.row) return;
    try {
      await deleteService(deleteTarget.row.service_id);
      toast.success("Service deleted successfully.");
      fetchServices();
    } catch (err) {
      console.error(err);
      toast.error(err?.message || "Failed to delete service.");
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      if (selectedService) {
        await updateService(selectedService.service_id, serviceFormPayload);
      } else {
        await createService(serviceFormPayload);
      }
      setOpenModal(false);
      fetchServices();
    } catch (err) {
      console.error("Failed to save service", err);
    }
  };

  const columns = [
    { header: "Service ID", accessor: "service_id" },
    { header: "Service Name", accessor: "service_name" },
    {
      header: "Society",
      render: (row) =>
        row.societies?.society_name ||
        societies.find((s) => Number(s.society_id) === Number(row.society_id))?.society_name ||
        "Global",
    },
    {
      header: "Action",
      render: (row) => (
        <ActionButtons
          row={row}
          onEdit={() => handleEdit(row)}
          onDelete={() => handleDelete(row)}
        />
      ),
    },
  ];

  return (
    <AdminLayout>
      <DataTableLayout
        title="Services Management"
        filters={
          <FilterBar
            filtersConfig={[
              {
                key: "search",
                label: "Search",
                type: "text",
                placeholder: "Search service...",
              },
              {
                key: "society_id",
                label: "Property",
                type: "select",
                options: [
                  { value: "", label: "All Properties (Master Catalog)" },
                  { value: "global", label: "Global Master Services" },
                  ...societies.map((s) => ({
                    value: String(s.society_id),
                    label: s.society_name,
                  })),
                ],
              },
            ]}
            filters={filters}
            setFilters={setFilters}
            onApply={() => setAppliedFilters(filters)}
            onClear={() => {
              const empty = { search: "", society_id: "" };
              setFilters(empty);
              setAppliedFilters(empty);
            }}
          />
        }
        actions={
          <Button variant="primary" icon={<Plus size={16} />} onClick={handleAdd}>
            Add Service
          </Button>
        }
      >
        {loading ? (
          <Skeleton variant="list" count={3} />
        ) : (
          <DataTable columns={columns} data={filteredData} />
        )}
      </DataTableLayout>

      <Modal
        isOpen={openModal}
        onClose={() => setOpenModal(false)}
        title={selectedService ? "Edit Service" : "Add Service"}
      >
        <form onSubmit={handleSave} style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          <FormField label="Service Name" required>
            <TextInput
              placeholder="e.g. Electrician, Plumbing, Deep Cleaning"
              value={serviceFormPayload.service_name}
              onChange={(e) =>
                setServiceFormPayload({ ...serviceFormPayload, service_name: e.target.value })
              }
              required
            />
          </FormField>
          <FormField label="Property Scope" hint="Leave as Global for society-wide availability">
            <SelectDropdown
              value={serviceFormPayload.society_id || ""}
              onChange={(e) =>
                setServiceFormPayload({ ...serviceFormPayload, society_id: e.target.value })
              }
              options={[
                { label: "Global (Available to All Properties)", value: "" },
                ...societies.map((s) => ({
                  label: `${s.society_name} (${s.property_type?.name || "Property"})`,
                  value: String(s.society_id),
                }))
              ]}
            />
          </FormField>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: "12px", marginTop: "8px" }}>
            <Button variant="outline" onClick={() => setOpenModal(false)} type="button">
              Cancel
            </Button>
            <Button type="submit" variant="primary">
              {selectedService ? "Save Changes" : "Create Service"}
            </Button>
          </div>
        </form>
      </Modal>

      <DeleteConfirmationModal
        isOpen={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        entityType={deleteTarget?.type || "Service"}
        entityId={deleteTarget?.id}
        entityName={deleteTarget?.name || ""}
        canDelete={deleteTarget?.canDelete !== false}
        actionLabel={deleteTarget?.actionLabel || "Delete"}
        onConfirm={handleConfirmDelete}
      />
    </AdminLayout>
  );
};

export default Services;
